/** @jsxImportSource theme-ui */
import { jsx, Flex, Box } from "theme-ui";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import { Header } from "../../resource";
import { useResponsiveValue } from "../../../../../shared-utils/src/components/utils/media-query";
import { colors } from "../../../styles/theme";
import { Container } from "../../../../src/components/organisms";

const Banner = ({
  image,
  imageAlt,
  title,
  subTitle,
  height,
  borderRadii,
  circleStyles,
  imageWidth,
  imageBorderRadius,
  url,
  author,
  timeToRead,
}) => (
  <>
    <Container variant="pillar">
      <Header
        title={title}
        subTitle={subTitle}
        customStyles={{ mt: "1.5rem" }}
        url={url}
        author={author}
        timeToRead={timeToRead}
      />
    </Container>

    <Flex sx={bannerStyles.flexContainer}>
      <Box
        sx={{
          ...bannerStyles.box,
          height: height,
          borderTopLeftRadius: borderRadii,
          borderBottomLeftRadius: borderRadii,
          ml: ["1rem", "1rem", "2rem", "3.12rem"],
        }}
      >
        <Box sx={{ ...bannerStyles.circle, ...circleStyles[0] }} />
        <Box sx={{ ...bannerStyles.circle, ...circleStyles[1] }} />
      </Box>
      <GatsbyImage
        image={getImage(image)}
        style={{
          borderRadius: imageBorderRadius,
          width: imageWidth,
          zIndex: 1,
        }}
        alt={imageAlt}
      />
      <Box
        sx={{
          ...bannerStyles.box,
          height: height,
          borderTopRightRadius: borderRadii,
          borderBottomRightRadius: borderRadii,
          mr: ["1rem", "1rem", "2rem", "3.12rem"],
        }}
      >
        <Box sx={{ ...bannerStyles.circle, ...circleStyles[2] }} />
      </Box>
    </Flex>
  </>
);

const MobileBanner = (props) => (
  <Banner {...props} {...bannerSizeStyles.small} />
);
const MediumBanner = (props) => (
  <Banner {...props} {...bannerSizeStyles.medium} />
);
const LargeBanner = (props) => (
  <Banner {...props} {...bannerSizeStyles.large} />
);
const ExtraLargeBanner = (props) => (
  <Banner {...props} {...bannerSizeStyles.extraLarge} />
);

const PillarBanner = ({
  image,
  imageAlt,
  title,
  subTitle,
  url,
  author,
  timeToRead,
}) => {
  const bannerComponents = [
    <MobileBanner
      image={image}
      imageAlt={imageAlt}
      title={title}
      subTitle={subTitle}
      url={url}
      author={author}
      timeToRead={timeToRead}
    />,
    <MobileBanner
      image={image}
      imageAlt={imageAlt}
      title={title}
      subTitle={subTitle}
      url={url}
      author={author}
      timeToRead={timeToRead}
    />,
    <MediumBanner
      image={image}
      imageAlt={imageAlt}
      title={title}
      subTitle={subTitle}
      url={url}
      author={author}
      timeToRead={timeToRead}
    />,
    <LargeBanner
      image={image}
      imageAlt={imageAlt}
      title={title}
      subTitle={subTitle}
      url={url}
      author={author}
      timeToRead={timeToRead}
    />,
    <ExtraLargeBanner
      image={image}
      imageAlt={imageAlt}
      title={title}
      subTitle={subTitle}
      url={url}
      author={author}
      timeToRead={timeToRead}
    />,
  ];

  const pillarBannerComponent = useResponsiveValue(bannerComponents);

  return pillarBannerComponent;
};

const bannerStyles = {
  flexContainer: {
    mt: "3rem",
    alignItems: "center",
  },
  box: {
    position: "relative",
    flex: 1,
    backgroundColor: colors.secondaryGreenMain,
  },
  circle: {
    position: "absolute",
    backgroundColor: colors.primaryMain,
  },
};

const bannerSizeStyles = {
  small: {
    height: "4.9rem",
    borderRadii: "24px",
    imageWidth: "78%",
    imageBorderRadius: "24px",
    circleStyles: [
      {
        top: "1.3rem",
        left: "0.3rem",
        width: "1rem",
        height: "1rem",
        borderRadius: "50%",
      },
      {
        top: "3rem",
        left: "0.4rem",
        width: "0.5rem",
        height: "0.5rem",
        borderRadius: "50%",
      },
      {
        bottom: "0",
        right: "0",
        width: "1.4rem",
        height: "2.5rem",
        borderTopLeftRadius: "415px",
        borderBottomLeftRadius: "0",
        borderTopRightRadius: "0",
        borderBottomRightRadius: "320px",
      },
    ],
  },
  medium: {
    height: "10rem",
    borderRadii: "24px",
    imageWidth: "78%",
    imageBorderRadius: "24px",
    circleStyles: [
      {
        top: "2.5rem",
        left: "0.8rem",
        width: "2.5rem",
        height: "2.5rem",
        borderRadius: "50%",
      },
      {
        top: "6rem",
        left: "0.5rem",
        width: "1rem",
        height: "1rem",
        borderRadius: "50%",
      },
      {
        bottom: "0",
        right: "0",
        width: "4.2rem",
        height: "6.5rem",
        borderTopLeftRadius: "415px",
        borderBottomLeftRadius: "0",
        borderTopRightRadius: "0",
        borderBottomRightRadius: "140px",
      },
    ],
  },
  large: {
    height: "15rem",
    borderRadii: "24px",
    imageWidth: "77%",
    imageBorderRadius: "24px",
    circleStyles: [
      {
        top: "3rem",
        left: "1rem",
        width: "5rem",
        height: "5rem",
        borderRadius: "50%",
      },
      {
        top: "10rem",
        left: "0.5rem",
        width: "1.813rem",
        height: "1.813rem",
        borderRadius: "50%",
      },
      {
        bottom: "0",
        right: "0",
        width: "6rem",
        height: "7.5rem",
        borderTopLeftRadius: "415px",
        borderBottomLeftRadius: "0",
        borderTopRightRadius: "0",
        borderBottomRightRadius: "60px",
      },
    ],
  },
  extraLarge: {
    height: "15rem",
    borderRadii: "24px",
    imageWidth: "55%",
    imageBorderRadius: "32px",
    circleStyles: [
      {
        top: "2rem",
        left: "6rem",
        width: "7.625rem",
        height: "7.625rem",
        borderRadius: "50%",
      },
      {
        top: "9.5rem",
        left: "4.5rem",
        width: "1.813rem",
        height: "1.813rem",
        borderRadius: "50%",
      },
      {
        bottom: "0",
        right: "0",
        width: "11rem",
        height: "12.5rem",
        borderTopLeftRadius: "415px",
        borderBottomLeftRadius: "0",
        borderTopRightRadius: "0",
        borderBottomRightRadius: "60px",
      },
    ],
  },
};

export default PillarBanner;
